/**
 * 404, and the fallback for any unmatched route.
 *
 * It used to render no site header, no footer, and a palette from a starter
 * template — slate greys, a blue button, a pulsing red alert circle — none of
 * which appear anywhere else on this site. So the page a visitor hits after a
 * mistyped URL or a dead link from an old email looked like a different
 * product, and the only way onward was one button.
 *
 * It is also what the fifteen /resources/<tool> route files render when a slug
 * has no path, which makes it a page a signed-in member can land on without
 * having typed anything wrong at all.
 *
 * Now it is an ordinary page of this site: the same header, the same footer,
 * the same buttons — and three ways out rather than one, because "go home" is
 * the right answer only if home is where they were trying to get.
 */

import { Link } from "wouter";
import { Compass } from "lucide-react";
import { BUTTON } from "@/lib/buttonStyles";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

export default function NotFound() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader context="Page not found" />

      <main className="flex-1">
        <div className="container max-w-3xl py-16 sm:py-24">
          <div className="rounded-xl border border-border border-l-4 border-l-primary bg-card p-6 sm:p-8">
            <span
              aria-hidden="true"
              className="inline-grid place-items-center w-12 h-12 rounded-full bg-primary/10 text-primary border border-primary"
            >
              <Compass className="w-6 h-6" />
            </span>

            <h1 className="mt-5 font-['Fraunces'] text-3xl sm:text-4xl font-semibold text-foreground leading-tight tracking-tight">
              That page isn&rsquo;t here.
            </h1>

            {/* No "404" as the headline and no error styling. The number means
                nothing to the audience this site is written for, and a red
                alert reads as "you have broken something" when the usual cause
                is a link in an email older than the page it points at. */}
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">
              It may have moved, or the link that brought you here may be out of
              date. Nothing you saved is affected &mdash; your stack and your
              progress are on your account.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link href="/" className={BUTTON.primarySm}>
                Back to the Roles Map
              </Link>
              <Link
                href="/resources"
                className="inline-flex items-center justify-center min-h-11 px-5 rounded-md border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
              >
                Browse the Resource Center
              </Link>
              <Link
                href="/my-stack"
                className="inline-flex items-center justify-center min-h-11 px-5 rounded-md border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
              >
                My Stack
              </Link>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
